import { Create, useForm } from '@refinedev/antd';
import { HttpError } from '@refinedev/core';
import { Form, Input, DatePicker, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Conference } from '../../api/types';
import { assignAllReps } from '../../api/discovery';
import { useRoleClaim } from '../../auth/useRoleClaim';

interface CreateConferenceInput {
  name: string;
  location?: string | null;
  startDate: string;
  endDate: string;
}

interface FormShape {
  name: string;
  location?: string;
  startDate: Dayjs;
  endDate: Dayjs;
}

export function ConferenceCreate() {
  const { companyId } = useRoleClaim();
  const { formProps, saveButtonProps, onFinish } = useForm<
    Conference,
    HttpError,
    CreateConferenceInput
  >({
    resource: 'conferences',
    action: 'create',
    // A company_admin's reps should see a brand-new conference right away, so
    // auto-assign the whole company on create. Portal-only — the console's
    // conference create deliberately assigns no one (super-admins scope reps
    // per tenant on the Assignments tab). Best-effort: a failure here does NOT
    // undo the created conference; the admin can fix it from Assignments.
    onMutationSuccess: async (data) => {
      const confId = (data?.data as Conference | undefined)?.id;
      if (!confId || !companyId) return;
      try {
        const { assigned } = await assignAllReps(confId, companyId);
        message.success(
          `Auto-assigned ${assigned} of your users to this conference.`,
        );
      } catch {
        message.warning(
          'Conference created, but auto-assigning your users failed — assign them from the Assignments tab.',
        );
      }
    },
  });

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values) => {
          const v = values as unknown as FormShape;
          return onFinish({
            name: v.name,
            location: v.location ?? null,
            startDate: v.startDate.format('YYYY-MM-DD'),
            endDate: v.endDate.format('YYYY-MM-DD'),
          });
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          extra="Choose a name that alphabetizes well and reads clearly, e.g. 2026-Infocom-Atlanta or 2026-ATD-LosAngeles."
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input maxLength={200} placeholder="2026-Infocom-Atlanta" />
        </Form.Item>
        <Form.Item label="Location" name="location">
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item
          label="Start date"
          name="startDate"
          rules={[{ required: true, message: 'Start date is required' }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="End date"
          name="endDate"
          dependencies={['startDate']}
          rules={[
            { required: true, message: 'End date is required' },
            ({ getFieldValue }) => ({
              validator(_, value: Dayjs | undefined) {
                const start = getFieldValue('startDate') as Dayjs | undefined;
                if (!value || !start) return Promise.resolve();
                return value.isBefore(start, 'day')
                  ? Promise.reject(new Error('End date must be on or after start'))
                  : Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Create>
  );
}

// Ensure dayjs is imported at runtime (keeps tree-shaking honest).
void dayjs;
